import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Search, 
  Users, 
  Building2, 
  Filter,
  TrendingUp,
  Clock,
  MoreHorizontal,
  Send,
  MessageSquare,
  Trash2,
  RefreshCw,
  X,
  Calendar
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@vaiu/ui/card";
import { Button } from "@vaiu/ui/button";
import { Input } from "@vaiu/ui/input";
import { Badge } from "@vaiu/ui/badge";
import { Label } from "@vaiu/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@vaiu/ui/popover";
import { Calendar as CalendarComponent } from "@vaiu/ui/calendar";
import { DateRange } from "react-day-picker";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@vaiu/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@vaiu/ui/dropdown-menu";
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { LoadingSpinner } from '@vaiu/ui';
import { ErrorMessage } from '@vaiu/ui';
import { platformApi, Contact, Transaction } from '@/services/platformApi';
import ContactList from '@/components/contacts/ContactList';
import ContactGroups from '@/components/contacts/ContactGroups';
import { i18n } from '@/utils/i18n';

/**
 * Calculate transaction totals grouped by currency for a contact
 * @param contact Contact with optional transactions array
 * @param userCurrency User's default currency for fallback
 * @returns Record of currency code to { sent, received } totals
 */
const calculateContactTotalsByCurrency = (
  contact: Contact,
  userCurrency: string
): Record<string, { sent: number; received: number }> => {
  const totalsByCurrency: Record<string, { sent: number; received: number }> = {};

  // If transactions array is available, calculate from it
  if (contact.transactions && contact.transactions.length > 0) {
    contact.transactions.forEach((transaction: Transaction) => {
      const amount = typeof transaction.amount === 'string' 
        ? parseFloat(transaction.amount) 
        : transaction.amount || 0;
      
      if (isNaN(amount) || amount === 0) return;
      
      // Get transaction currency, fallback to user's currency
      const txCurrency = transaction.currency?.toUpperCase() || 
                        transaction.data?.currency?.toUpperCase() || 
                        userCurrency;
      
      // Initialize currency totals if not exists
      if (!totalsByCurrency[txCurrency]) {
        totalsByCurrency[txCurrency] = { sent: 0, received: 0 };
      }
      
      // Handle different transaction type formats
      const type = transaction.type?.toLowerCase() || '';
      
      // Determine if this transaction involves the contact as counterparty
      // Match by contact ID (counterparty ID, recipient ID, or originator)
      const isCounterparty = transaction.counterparty?.id === contact.id || 
                             transaction.recipientId === contact.id ||
                             transaction.originator === contact.id ||
                             // Match by merchant ID if contact is a merchant
                             (contact.merchantId && (
                               transaction.counterparty?.id === contact.merchantId ||
                               transaction.recipientId === contact.merchantId ||
                               transaction.originator === contact.merchantId
                             )) ||
                             // Match by counterparty name (case-insensitive)
                             (transaction.counterparty?.name && contact.name &&
                              transaction.counterparty.name.toLowerCase() === contact.name.toLowerCase()) ||
                             // Match by recipient name (case-insensitive)
                             (transaction.recipientName && contact.name &&
                              transaction.recipientName.toLowerCase() === contact.name.toLowerCase());
      
      if (isCounterparty) {
        // If we sent TO the contact (send/debit/transfer), add to sent
        if (type === 'send' || type === 'debit' || type === 'transfer') {
          totalsByCurrency[txCurrency].sent += Math.abs(amount);
        } 
        // If we received FROM the contact (receive/credit/deposit), add to received
        else if (type === 'receive' || type === 'credit' || type === 'deposit') {
          totalsByCurrency[txCurrency].received += Math.abs(amount);
        }
        // Handle redeem type - if we redeemed a token from this contact, we received from them
        else if (type === 'redeem') {
          totalsByCurrency[txCurrency].received += Math.abs(amount);
        }
      }
    });
  }
  
  // Fallback: if no transactions calculated but contact totals exist, use them for user's currency
  if (Object.keys(totalsByCurrency).length === 0 && (contact.totalSent > 0 || contact.totalReceived > 0)) {
    totalsByCurrency[userCurrency] = {
      sent: contact.totalSent || 0,
      received: contact.totalReceived || 0
    };
  }

  return totalsByCurrency;
};

const Contacts: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  
  // Get user's default currency, fallback to USD
  const userCurrency = (typeof user?.currency === 'string' ? user.currency : 'USD') || 'USD';
  
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rawApiResponse, setRawApiResponse] = useState<any>(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [merchantProfilesCache, setMerchantProfilesCache] = useState<Map<string, any>>(new Map());
  const [fetchingMerchants, setFetchingMerchants] = useState(false);
  const [fetchingContacts, setFetchingContacts] = useState(false);
  
  // Use ref to track cache and prevent stale closures
  const merchantProfilesCacheRef = useRef<Map<string, any>>(new Map());
  const fetchingMerchantsRef = useRef(false); // Use ref to prevent race conditions
  const merchantProfilesFetchAttemptedRef = useRef(false); // Track if we've already attempted to fetch for current contacts
  
  // Keep ref in sync with state
  useEffect(() => {
    merchantProfilesCacheRef.current = merchantProfilesCache;
  }, [merchantProfilesCache]);
  
  // Keep fetching flag ref in sync
  useEffect(() => {
    fetchingMerchantsRef.current = fetchingMerchants;
  }, [fetchingMerchants]);
  
  // Reset fetch attempt flag when contacts are actually loaded/changed
  // Use a ref to track the last contact IDs to detect actual changes
  const lastContactIdsRef = useRef<string>('');
  
  useEffect(() => {
    // Create a signature of current contacts (IDs only)
    const currentContactIds = contacts.map(c => c.id).sort().join(',');
    
    // Only reset if contacts actually changed (not just re-render)
    if (currentContactIds !== lastContactIdsRef.current && contacts.length > 0) {
      lastContactIdsRef.current = currentContactIds;
      merchantProfilesFetchAttemptedRef.current = false;
      console.log('🔄 [Contacts] Contacts changed, resetting merchant profile fetch flag');
    }
  }, [contacts]);
  
  // Calculate totals by currency for all contacts (memoized for performance)
  const contactsTotalsByCurrency = useMemo(() => {
    const totalsMap = new Map<string, Record<string, { sent: number; received: number }>>();
    contacts.forEach(contact => {
      totalsMap.set(contact.id, calculateContactTotalsByCurrency(contact, userCurrency));
    });
    return totalsMap;
  }, [contacts, userCurrency]);
  
  // Search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [sortBy, setSortBy] = useState<'name' | 'frequency' | 'time'>('name');
  
  // Advanced filters
  const [showFilters, setShowFilters] = useState(false);
  const [minAmount, setMinAmount] = useState<string>('');
  const [maxAmount, setMaxAmount] = useState<string>('');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  
  // Caching
  const [contactsLoaded, setContactsLoaded] = useState(false);
  
  // Modals and selections
  const [showContactGroups, setShowContactGroups] = useState(false);
  
  // Cache key for localStorage
  const CACHE_KEY = `contacts_cache_${user?.id || 'anonymous'}`;
  const CACHE_TIMESTAMP_KEY = `contacts_cache_timestamp_${user?.id || 'anonymous'}`;

  // Define fetchMerchantProfiles first (before loadContactsFromCache)
  const fetchMerchantProfiles = React.useCallback(async (contactsToProcess: Contact[]) => {
    // Prevent multiple calls - check both ref and if we've already attempted for this contact set
    if (fetchingMerchantsRef.current) {
      console.log('⚠️ [Contacts] fetchMerchantProfiles already in progress, skipping');
      return;
    }
    
    if (merchantProfilesFetchAttemptedRef.current) {
      console.log('⚠️ [Contacts] Merchant profiles fetch already attempted for current contacts, skipping');
      return;
    }
    
    // Mark as attempted immediately to prevent duplicate calls
    merchantProfilesFetchAttemptedRef.current = true;
    
    // Use ref to get current cache state (avoids stale closure)
    const currentCache = merchantProfilesCacheRef.current;
    
    const businessContactIds = contactsToProcess
      .filter(c => c.relationship === 'business' && c.merchantId)
      .map(c => c.merchantId!)
      .filter((id, index, self) => self.indexOf(id) === index)
      .filter(id => !currentCache.has(id));
    
    if (businessContactIds.length === 0) {
      console.log('✅ [Contacts] All merchant profiles already cached');
      return; // No new merchants to fetch
    }
    
    // Set both state and ref immediately to prevent race conditions
    fetchingMerchantsRef.current = true;
    setFetchingMerchants(true);
    
    try {
      console.log(`📦 [Contacts] Fetching ${businessContactIds.length} merchant profiles in batch`);
      const batchResult = await platformApi.merchant.getPublicMerchantProfilesBatch(businessContactIds);
      
      // Update cache using functional update to ensure we have latest state
      setMerchantProfilesCache(prevCache => {
        const updatedCache = new Map(prevCache);
        batchResult.merchants.forEach(merchant => {
          updatedCache.set(merchant.id, merchant);
        });
        console.log(`✅ [Contacts] Cached ${batchResult.merchants.length} merchant profiles`);
        return updatedCache;
      });
    } catch (error) {
      console.error('Failed to fetch merchant profiles:', error);
      // Don't retry on rate limit errors - reset the attempted flag so we don't block future attempts
      if (error instanceof Error && error.message.includes('429')) {
        console.warn('⚠️ [Contacts] Rate limited, will skip merchant profile fetch');
        merchantProfilesFetchAttemptedRef.current = false; // Allow retry after rate limit clears
      }
    } finally {
      fetchingMerchantsRef.current = false;
      setFetchingMerchants(false);
    }
  }, []); // No dependencies - uses refs to avoid stale closures

  // Define fetchContacts before loadContactsFromCache (wrap in useCallback for stability)
  const fetchContacts = React.useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (fetchingContacts) {
      console.log('⚠️ [Contacts] fetchContacts already in progress, skipping');
      return;
    }
    
    try {
      setFetchingContacts(true);
      setLoading(true);
      setError(null);
      
      // Store raw response before mapping (we'll need to capture this from the API client)
      const contactsData = await platformApi.getContacts({ includeTransactions: true });
      
      // Get raw API response for debug panel
      const rawResponse = platformApi.contacts.getRawApiResponse();
      setRawApiResponse(rawResponse);
      
      // DEBUG: Log received contacts data
      console.log('🔍 [Contacts] Received contacts data:', contactsData);
      console.log('🔍 [Contacts] Raw API response:', rawResponse);
      console.log('🔍 [Contacts] Contacts count:', contactsData?.length);
      if (contactsData && contactsData.length > 0) {
        console.log('🔍 [Contacts] First contact:', JSON.stringify(contactsData[0], null, 2));
        console.log('🔍 [Contacts] First contact transaction info:', {
          totalTransactions: contactsData[0].totalTransactions,
          lastTransactionDate: contactsData[0].lastTransactionDate,
          firstTransactionDate: contactsData[0].firstTransactionDate,
          totalSent: contactsData[0].totalSent,
          totalReceived: contactsData[0].totalReceived
        });
      }
      
      // Ensure we have an array
      if (Array.isArray(contactsData)) {
        setContacts(contactsData);
        setContactsLoaded(true);
        
        // Cache contacts in localStorage
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(contactsData));
          localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
          console.log('✅ [Contacts] Cached contacts');
        } catch (cacheError) {
          console.warn('Failed to cache contacts:', cacheError);
        }
        
        // Batch fetch merchant profiles for business contacts
        // Only fetch if not already fetching (prevent duplicate calls)
        if (!fetchingMerchantsRef.current) {
          await fetchMerchantProfiles(contactsData);
        } else {
          console.log('⚠️ [Contacts] Skipping merchant profile fetch - already in progress');
        }
      } else {
        console.warn('Contacts API returned non-array data:', contactsData);
        setContacts([]);
        setContactsLoaded(true);
      }
    } catch (err: any) {
      console.error('Error in fetchContacts:', err);
      
      // Provide more specific error messages
      let errorMessage = 'Unable to load contacts. Please try again later.';
      if (err?.message) {
        if (err.message.includes('401') || err.message.includes('Authentication')) {
          errorMessage = 'Please log in to view your contacts.';
        } else if (err.message.includes('404')) {
          errorMessage = 'Contacts endpoint not found.';
        } else if (err.message.includes('429')) {
          errorMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (err.message.includes('500')) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      setContacts([]); // Ensure contacts is always an array
    } finally {
      setLoading(false);
      setFetchingContacts(false);
    }
  }, [fetchMerchantProfiles]); // Only depends on fetchMerchantProfiles

  // Define loadContactsFromCache after fetchContacts and fetchMerchantProfiles
  const loadContactsFromCache = React.useCallback(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (cached && timestamp) {
        const cacheAge = Date.now() - parseInt(timestamp, 10);
        // Use cache if less than 7 days old
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
        if (cacheAge < sevenDaysInMs) {
          const cachedContacts = JSON.parse(cached);
          setContacts(cachedContacts);
          setContactsLoaded(true);
          setLoading(false);
          console.log('✅ [Contacts] Loaded from cache');
          
          // Still fetch merchant profiles for business contacts (only if not already attempted)
          if (!merchantProfilesFetchAttemptedRef.current) {
            fetchMerchantProfiles(cachedContacts);
          }
          return;
        }
      }
      
      // No cache or expired, fetch fresh data
      if (!fetchingContacts) {
        fetchContacts();
      }
    } catch (error) {
      console.error('Failed to load contacts from cache:', error);
      // Fallback to fetching
      if (!fetchingContacts) {
        fetchContacts();
      }
    }
  }, [fetchContacts, fetchMerchantProfiles]);

  // Define filterAndSortContacts before useEffect that uses it
  const filterAndSortContacts = React.useCallback(() => {
    let filtered = [...contacts];

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(contact =>
        contact.name.toLowerCase().includes(search) ||
        contact.email?.toLowerCase().includes(search) ||
        contact.phoneNumber?.includes(search)
      );
    }

    // Filter by amount range
    if (minAmount || maxAmount) {
      filtered = filtered.filter(contact => {
        const totalAmount = (contact.totalSent || 0) + (contact.totalReceived || 0);
        const min = minAmount ? parseFloat(minAmount) : 0;
        const max = maxAmount ? parseFloat(maxAmount) : Infinity;
        return totalAmount >= min && totalAmount <= max;
      });
    }

    // Filter by date range
    if (dateRange?.from || dateRange?.to) {
      filtered = filtered.filter(contact => {
        if (!contact.lastTransactionDate) return false;
        const lastTxDate = new Date(contact.lastTransactionDate);
        const fromDate = dateRange.from ? new Date(dateRange.from) : new Date(0);
        const toDate = dateRange.to ? new Date(dateRange.to) : new Date();
        // Set time to start/end of day for proper comparison
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(23, 59, 59, 999);
        return lastTxDate >= fromDate && lastTxDate <= toDate;
      });
    }

    // Filter by tab
    switch (activeTab) {
      case 'business':
        filtered = filtered.filter(c => c.relationship === 'business');
        break;
      case 'infrequent':
        filtered = filtered.filter(c => c.relationship === 'infrequent');
        break;
      // 'all' shows everything
    }

    // Sort contacts
    switch (sortBy) {
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'time':
        filtered.sort((a, b) => {
          const aDate = a.lastTransactionDate ? new Date(a.lastTransactionDate).getTime() : 0;
          const bDate = b.lastTransactionDate ? new Date(b.lastTransactionDate).getTime() : 0;
          return bDate - aDate; // Most recent first
        });
        break;
      case 'frequency':
        filtered.sort((a, b) => b.totalTransactions - a.totalTransactions); // Most tokens first
        break;
    }

    setFilteredContacts(filtered);
  }, [contacts, searchTerm, activeTab, sortBy, minAmount, maxAmount, dateRange]);

  // useEffect that uses loadContactsFromCache - must be after loadContactsFromCache is defined
  useEffect(() => {
    // Wait for auth to finish loading before checking user
    if (authLoading) {
      return;
    }
    
    // Only redirect to login if auth has finished loading and user is still null
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Load from cache first, then fetch if cache is empty or expired
    // Only load once - use contactsLoaded flag to prevent multiple calls
    if (!contactsLoaded && contacts.length === 0 && !fetchingContacts) {
      loadContactsFromCache();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate, contactsLoaded, contacts.length, fetchingContacts, loadContactsFromCache]); // Include all dependencies

  useEffect(() => {
    filterAndSortContacts();
  }, [filterAndSortContacts]);
  
  const handleRefresh = async () => {
    // Clear cache and fetch fresh data
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP_KEY);
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
    setContactsLoaded(false);
    await fetchContacts();
  };
  
  const clearFilters = () => {
    setMinAmount('');
    setMaxAmount('');
    setDateRange(undefined);
    setSearchTerm('');
  };
  
  const hasActiveFilters = () => {
    return !!(minAmount || maxAmount || dateRange?.from || dateRange?.to || searchTerm.trim());
  };

  const handleContactSelect = (contact: Contact) => {
    // Navigate to contact detail page
    navigate(`/contacts/${contact.id}`);
  };


  const handleSendMoney = (contact: Contact) => {
    // Navigate to pay page with payeeId only (security: name must come from server lookup)
    // Use merchantId for business contacts, otherwise use contact.id
    const payeeId = contact.merchantId || contact.id;
    navigate(`/pay?payeeId=${payeeId}`);
  };

  const handleViewMessages = (contact: Contact, transactionId?: string) => {
    if (transactionId) {
      // Navigate to transaction-based conversation
      navigate(`/messages?transactionId=${transactionId}&contactId=${contact.id}`, {
        state: { contactName: contact.name }
      });
    } else {
      // Navigate to direct message or most recent transaction
      if (contact.transactions && contact.transactions.length > 0) {
        // Get most recent transaction
        const recentTransaction = contact.transactions[0];
        const txId = typeof recentTransaction === 'string' 
          ? recentTransaction 
          : recentTransaction.id || recentTransaction._id || recentTransaction.key;
        
        if (txId) {
          navigate(`/messages?transactionId=${txId}&contactId=${contact.id}`, {
            state: { contactName: contact.name }
          });
        } else {
          navigate(`/messages?contactId=${contact.id}`, {
            state: { contactName: contact.name }
          });
        }
      } else {
        // Direct message (no transactions)
        navigate(`/messages?contactId=${contact.id}`, {
          state: { contactName: contact.name }
        });
      }
    }
  };

  const getTabCounts = () => {
    return {
      all: contacts.length,
      business: contacts.filter(c => c.relationship === 'business').length,
      infrequent: contacts.filter(c => c.relationship === 'infrequent').length,
    };
  };

  const tabCounts = getTabCounts();

  // Show loading spinner while auth is loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Redirect to login if not authenticated (after auth has finished loading)
  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center space-x-3">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/')}
              className="p-2"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{i18n.__('contacts.title')}</h1>
              <p className="text-sm text-muted-foreground">
                {contacts.length} {contacts.length === 1 ? 'contact' : 'contacts'} • {filteredContacts.length} shown
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={fetchingContacts}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${fetchingContacts ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {import.meta.env.DEV && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDebugPanel(!showDebugPanel)}
              >
                {showDebugPanel ? 'Hide' : 'Show'} Debug
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowContactGroups(true)}
            >
              <Users className="w-4 h-4 mr-2" />
              {i18n.__('contacts.groups')}
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="w-4 h-4 mr-2" />
                  Sort
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSortBy('name')}>
                  <span className="mr-2">A-Z</span>
                  Name
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('frequency')}>
                  <TrendingUp className="w-4 h-4 mr-2" />
                  By Frequency
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('time')}>
                  <Clock className="w-4 h-4 mr-2" />
                  By Time
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input 
              placeholder={i18n.__('contacts.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Advanced Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={showFilters ? "default" : "outline"}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters() && (
                <Badge variant="secondary" className="ml-2">
                  Active
                </Badge>
              )}
            </Button>
            
            {hasActiveFilters() && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
              >
                <X className="w-4 h-4 mr-2" />
                Clear
              </Button>
            )}
          </div>
          
          {showFilters && (
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Amount Range */}
                  <div className="space-y-2">
                    <Label>Amount Range</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={minAmount}
                        onChange={(e) => setMinAmount(e.target.value)}
                        className="flex-1"
                      />
                      <span className="text-muted-foreground">to</span>
                      <Input
                        type="number"
                        placeholder="Max"
                        value={maxAmount}
                        onChange={(e) => setMaxAmount(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  
                  {/* Date Range */}
                  <div className="space-y-2">
                    <Label>Date Range</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {dateRange?.from ? (
                            dateRange.to ? (
                              <>
                                {dateRange.from.toLocaleDateString()} - {dateRange.to.toLocaleDateString()}
                              </>
                            ) : (
                              dateRange.from.toLocaleDateString()
                            )
                          ) : (
                            <span className="text-muted-foreground">Select date range</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          initialFocus
                          mode="range"
                          defaultMonth={dateRange?.from}
                          selected={dateRange}
                          onSelect={(range) => setDateRange(range)}
                          numberOfMonths={2}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>


        {/* Contact Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="all" className="flex items-center space-x-1">
              <span>{i18n.__('contacts.tabs.all')}</span>
              <Badge variant="secondary" className="ml-1 text-xs">
                {tabCounts.all}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="business" className="flex items-center space-x-1">
              <Building2 className="w-3 h-3" />
              <span>{i18n.__('contacts.tabs.business')}</span>
              <Badge variant="secondary" className="ml-1 text-xs">
                {tabCounts.business}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="infrequent" className="flex items-center space-x-1">
              <span>{i18n.__('contacts.tabs.others')}</span>
              <Badge variant="secondary" className="ml-1 text-xs">
                {tabCounts.infrequent}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8">
                    <LoadingSpinner text={i18n.__('contacts.loading')} />
                  </div>
                ) : error ? (
                  <div className="p-8">
                    <ErrorMessage
                      message={error}
                      onRetry={fetchContacts}
                    />
                  </div>
                ) : (
                  <ContactList
                    contacts={filteredContacts}
                    onContactSelect={handleContactSelect}
                    onSendMoney={handleSendMoney}
                    totalsByCurrency={contactsTotalsByCurrency}
                    userCurrency={userCurrency}
                    emptyMessage={
                      searchTerm.trim() 
                        ? i18n.__('contacts.emptySearch')
                        : activeTab === 'all'
                        ? i18n.__('contacts.emptyAll')
                        : i18n.__('contacts.emptyTab', { tab: activeTab })
                    }
                    merchantProfilesCache={merchantProfilesCache}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>



        {/* Contact Groups Modal */}
        {showContactGroups && (
          <ContactGroups
            isOpen={showContactGroups}
            onClose={() => setShowContactGroups(false)}
            contacts={contacts}
          />
        )}

        {/* Debug Panel */}
        {import.meta.env.DEV && showDebugPanel && rawApiResponse && (
          <Card className="mt-6 border-2 border-yellow-500">
            <CardHeader>
              <CardTitle className="text-yellow-700 dark:text-yellow-400">
                🔍 Debug: Raw API Response
              </CardTitle>
              <CardDescription>
                Raw data received from GET /api/v2/contacts endpoint
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto max-h-96 font-mono text-xs">
                <pre>{JSON.stringify(rawApiResponse, null, 2)}</pre>
              </div>
              <div className="mt-4 space-y-2">
                <p className="text-sm font-semibold">Response Structure:</p>
                <ul className="text-sm space-y-1 list-disc list-inside">
                  <li>Total contacts: {rawApiResponse?.total ?? 'N/A'}</li>
                  <li>Contacts array length: {rawApiResponse?.contacts?.length ?? 0}</li>
                  {rawApiResponse?.contacts && rawApiResponse.contacts.length > 0 && (
                    <>
                      <li>First contact keys: {Object.keys(rawApiResponse.contacts[0]).join(', ')}</li>
                      <li>First contact has transactions array: {Array.isArray(rawApiResponse.contacts[0]?.transactions) ? 'Yes' : 'No'}</li>
                      <li>First contact transactions count: {rawApiResponse.contacts[0]?.transactions?.length ?? 0}</li>
                      <li>First contact lastTransactionDate: {rawApiResponse.contacts[0]?.lastTransactionDate ?? 'Not present'}</li>
                      <li>First contact totalTransactions: {rawApiResponse.contacts[0]?.totalTransactions ?? 'Not present'}</li>
                    </>
                  )}
                </ul>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Contacts;
